import config from '../../config/config';

import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';
import putRequest from './base/put';

interface QueryParameters {
  limit?: number;
  [key: string]: any;
}

export default {
  /**
   * Fetching penalty management account data
   * @param query - The query parameters for fetching data
   * @returns - The result of the get request
   */
  fetchPenaltyManagementAccount: (query: QueryParameters) => {
    const parameters = { ...query, limit: query?.limit || 5 };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting`, {
      ...parameters,
    });
  },

  /**
   * Fetching penalty management account data
   * @param query - The query parameters for fetching data
   * @returns - The result of the get request
   */
  fetchPenaltyManagementStation: (query: QueryParameters) => {
    const parameter = { ...query, limit: query?.limit || 5 };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/penalty-management`, {
      ...parameter,
    });
  },
  /**
   * Fetch penalty management account filters list
   * @param query - The query parameters for fetching filters
   * @returns - The result of the get request
   */
  accountOverridesFiltersList: (query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/filter-list`,
      {
        ...parameter,
      },
    );
  },

  /**
   * Export penalty management account data
   * @param query - The query parameters for exporting data
   * @returns - The result of the get request
   */
  accountOverridesExport: (query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/export`,
      {
        ...parameter,
      },
    );
  },

  /**
   * Export penalty management account data
   * @param query - The query parameters for exporting data
   * @returns - The result of the get request
   */
  stationOverridesExport: (query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/penalty-management/export`, {
      ...parameter,
    });
  },

  /**
   * Fetch penalty management account filters list
   * @param query - The query parameters for fetching filters
   * @returns - The result of the get request
   */
  stationOverridesFiltersList: (query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/penalty-management/filter-list`, {
      ...parameter,
    });
  },

  /**
   * Adding penalty override
   * @param query - The data for adding override
   * @returns - The result of the post request
   */
  addAccountOverride: (query: QueryParameters) => {
    const parameter = { ...query };
    return postRequest(`${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting`, {
      ...parameter,
    });
  },

  /**
   * Adding penalty override
   * @param query - The data for adding override
   * @returns - The result of the post request
   */
  addStationOverride: (query: QueryParameters) => {
    const parameter = { ...query };
    return postRequest(`${config.commonApiUrl}api/super-admin/v1/penalty-management`, {
      ...parameter,
    });
  },

  /**
   * Updating single account status
   * @param id - The account ID to update
   * @returns The result of the patch request
   */
  updateSingleStationStatus: (id: string, status: boolean) => {
    return patchRequest(
      `${config.commonApiUrl}api/super-admin/v1/penalty-management/${id}/status`,
      { status },
    );
  },
  /**
   * Updating single account status
   * @param id - The account ID to update
   * @returns The result of the patch request
   */
  updateSingleAccountStatus: (id: string, status: boolean) => {
    return patchRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/${id}/status`,
      { status },
    );
  },

  /**
   * View single account details
   * @param id - The account ID to view
   * @returns The result of the get request
   */
  viewSingleAccountOverrideDetails: (id: string | null | undefined) => {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/${id}`,
    );
  },

  /**
   * View single account details
   * @param id - The account ID to view
   * @returns The result of the get request
   */
  viewSingleStationOverrideDetails: (id: string | null | undefined) => {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/penalty-management/${id}`);
  },

  /**
   * Update single account details
   * @param id - The account ID to update
   * @param query - The data to update
   * @returns The result of the put request
   */
  updateSingleAccountOverrideDetails: (id: string, query: QueryParameters) => {
    const parameters = { ...query };
    return patchRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/${id}`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Update single account details
   * @param id - The account ID to update
   * @param query - The data to update
   * @returns The result of the put request
   */
  updateSingleStationOverrideDetails: (id: string, query: QueryParameters) => {
    const parameters = { ...query };
    return patchRequest(`${config.commonApiUrl}api/super-admin/v1/penalty-management/${id}`, {
      ...parameters,
    });
  },

  /**
   * Find station names
   * @param id - The station ID
   * @returns - The result of the get request
   */
  findStationNames: (id: string) => {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-station/station-list/${id}`,
      {},
    );
  },

  /**
   * add Global Penalty Settings
   */
  createGlobalPenalty: (data: any): Promise<any> => {
    const parameters = { ...data };
    return putRequest(
      `${config.commonApiUrl}api/super-admin/v1/penalty-global-setting`,
      parameters,
    );
  },

  /**
   * Fetch global penalty settings quick view
   * @returns - The result of the get request
   */
  globalSettingQuickView: (): Promise<any> => {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/penalty-global-setting`);
  },

  /**
   * Fetching request's approved account list
   * @param query - The query parameters for fetching data
   */
  fetchRequestAccountByStatus: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query, limit: query?.limit };
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/facility-request`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetching request's approved station list
   * @param query - The query parameters for fetching data
   */
  fetchRequestStationByStatus: (query: QueryParameters = {}): Promise<any> => {
    const parameters = { ...query, limit: query?.limit };
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/penalty-management/facility-request`,
      {
        ...parameters,
      },
    );
  },

  /**
   * Fetching request's approved account filters
   */
  fetchRequestAccountFilters: () => {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/facility-request/filter-list`,
    );
  },

  /**
   * Fetching request's approved station filters
   */
  fetchRequestStationFilters: () => {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/penalty-management/facility-request/filter-list`,
    );
  },

  /**
   * Exporting request's approved account data
   * @param query - The query parameters for exporting data
   */
  exportAccountDataByStatus: (query: QueryParameters = {}): Promise<any> => {
    const parameter = { ...query };
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/facility-request-export`,
      {
        ...parameter,
      },
    );
  },

  /**
   * Exporting request's approved station data
   * @param query - The query parameters for exporting data
   */
  exportStationDataByStatus: (query: QueryParameters = {}): Promise<any> => {
    const parameter = { ...query };
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/penalty-management/facility-request-export`,
      {
        ...parameter,
      },
    );
  },

  /**
   * Fetching request's pending station filters
   */
  fetchPendingStationFilters: () => {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/filters`);
  },

  /**
   * Fetching request's rejected station filters
   */
  fetchRejectedStationFilters: () => {
    return getRequest(`${config.commonApiUrl}api/super-admin/v1/charging-station/filters`);
  },

  /**
   * create approved and rejected permission Api
   */
  createApprovedRejectedPermission: (data: any): Promise<any> => {
    const parameters = { ...data };
    return patchRequest(
      `${config.commonApiUrl}api/super-admin/v1/penalty-management/request-status`,
      parameters,
    );
  },
  /**
   * create approved and rejected permission Api
   */
  createAccountApprovedRejectedPermission: (data: any): Promise<any> => {
    const parameters = { ...data };
    return patchRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/request-status`,
      parameters,
    );
  },
  /**
   * Transaction list api
   */
  fetchPenaltyTransactionList: (query: QueryParameters) => {
    const parameter = { ...query, limit: query?.limit || 5 };
    return getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/penalty-management/transaction-list`,
      {
        ...parameter,
      },
    );
  },
  /**
   * Transaction list api
   */
  fetchPenaltyTransactionExport: (query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/penalty-management/transaction-list/export`,
      {
        ...parameter,
      },
    );
  },

  /**
   * Fetching transaction filter Api
   */
  fetchPenaltyTransactionFilter: () => {
    return getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/penalty-management/transaction-list/filter-list`,
    );
  },

  /**
   * Fetching card list api
   */
  fetchCardViewPrices: () => {
    return getRequest(
      `${config.transactionApiUrl}api/super-admin/v1/penalty-management/transaction-price-details`,
    );
  },
  /**
   * Audit log
   */
  fetchAuditLogList: (id: string, query: QueryParameters) => {
    const parameter = { ...query };
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/${id}/audit-log`,
      {
        ...parameter,
      },
    );
  },
  /**
   * fetch account name by id
   * @param id - The account ID to fetch name
   * @returns - The result of the get request
   */
  fetchAccountNameById: (id: string) => {
    return getRequest(
      `${config.accountApiUrl}api/super-admin/v1/facility-account/facility-account/${id}`,
    );
  },

  /**
   * fetch account name by id
   * @param id - The account ID to fetch name
   * @returns - The result of the get request
   */
  fetchAccountSetting: (id: string) => {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/facility-penalty-global-setting/exist-account-penalty/${id}`,
    );
  },
};
